import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  AppService,
  AuthenticationService,
  LOGGEDINUSER,
  PromptDialogService,
  UIConstants,
  UpdateUserSession,
  User,
  UserService,
} from '@lc/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AppbarComponent } from '@lc/ui/lib/appbar/appbar.component';

const BRAND_MAPPER = new Map([
  ['CBA', 'cb'],
  ['CBR', 'cb'],
  ['BHG', 'bhg'],
  ['ERA', 'era'],
]);
export class ViewModel {
  readonly firstName: string;
  readonly profilePhotoUrl: string;
  readonly showProfileLink: boolean = AppService.isCoordinatorApp || AppService.isAgentApp;
  readonly showFeedbackLink: boolean = AppService.isDCUserApp;
  readonly showWhatsNewLink: boolean = AppService.isCoordinatorApp;

  readonly showOnBehalfOf: boolean;

  constructor(readonly user: User, readonly assumedUser: User, isOBOEnabled: boolean) {
    this.firstName = user.getFirstName();
    this.profilePhotoUrl = user?.profile?.profilePhotoUrl || user?.profile?.photoUrl;

    this.showOnBehalfOf = isOBOEnabled;
  }
}

@Component({
  selector: 'lc-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  imports: [
    MatToolbar,
    MatToolbarRow,
    MatButton,
    MatIcon,
    AppbarComponent,
  ],
})
export class ToolbarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly store = inject<Store<any>>(Store);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthenticationService);
  private readonly dialogService = inject(PromptDialogService);

  showHeader = AppService.isDesignConciergeProduct || AppService.isListingConciergeProduct;
  viewModel$: Observable<ViewModel>;
  profilePhoto: string;
  firstName: string;
  lastName: string;
  id: string;
  showProfileLink: boolean;
  showWhatsNewLink: boolean;
  showOnBehalfOf: boolean;
  showFeedbackLink: boolean;
  getAssumedUser: User;
  oboUserAction$: Subscription;
  public brandKey: string;
  menuItems: any[];

  constructor() {
    const { userService } = this;

    const user$ = this.store.select(LOGGEDINUSER).pipe(map((user: User) => user));
    const assumedUser$ = user$.pipe(
      map(() => (this.userService.isAssumedUser() ? this.userService.getCurrentUser() : null)),
    );

    this.viewModel$ = combineLatest([user$, assumedUser$]).pipe(
      map(([user, assumedUser]) => new ViewModel(user, assumedUser, userService.isOBOEnabled())),
    );
    this.oboUserAction$ = this.userService.OBOUser.subscribe(
      (val) => {
        if (val) {
          this.menuItems = this.getMenuItems();
        }
      },
      (err) => {
        console.log(err);
      },
    );
  }

  ngOnInit(): void {
    this.viewModel$.subscribe({
      next: (res) => {
        this.profilePhoto = res.profilePhotoUrl;
        this.firstName = res.user.firstName;
        this.lastName = res.user.lastName;
        this.id = res.user.profile._id;
        this.showProfileLink = res.showProfileLink;
        this.showWhatsNewLink = res.showWhatsNewLink;
        this.showOnBehalfOf = res.showOnBehalfOf;
        this.showFeedbackLink = res.showFeedbackLink;
        this.getAssumedUser = res.assumedUser;
        this.brandKey = this.getBrandKey(res);
        this.menuItems = this.getMenuItems();
      },
      error: (errData) => {
        console.log(errData);
      },
    });
  }

  getBrandKey(res) {
    const brandCode = res.assumedUser?.profile?.office?.company?.brandCode
      ?? res.user?.profile?.office?.company?.brandCode;
    const brandKey = BRAND_MAPPER.get(brandCode) ?? 'cb';
    return brandKey;
  }

  getMenuItems() {
    const menuItems = [];
    if (this.showProfileLink) {
      menuItems.push({
        name: 'My Profile',
        icon: 'Person',
        url: `/my-profile/${this.id}`,
      });
    }
    if (this.showWhatsNewLink) {
      menuItems.push({
        name: "What's New",
        icon: 'Lightbulb',
        url: '/whats-new',
      });
    }
    if (this.showOnBehalfOf && !this.getAssumedUser) {
      menuItems.push({
        name: 'On Behalf of',
        url: '/on-behalf-of',
        icon: 'Group',
      });
    }
    if (this.showFeedbackLink) {
      menuItems.push({
        name: 'Feedback',
        url: '/feedback',
        icon: 'Chat',
      });
    }
    menuItems.push({
      name: 'Log Out',
      icon: 'Logout',
      onClick: () => {
        this.onLogout();
      },
    });
    return menuItems;
  }

  async endImpersonation() {
    await this.userService.endAssume();
    this.router
      .navigate(['/on-behalf-of'])
      .then(() => this.router.navigate(['/']))
      .then(() => window.location.reload());
  }

  /** Prompts the user if they want to logout. If confirmed, stops the expiration countdown and redirects to logout. */
  async onLogout() {
    const response = await this.dialogService.openPrompt(
      'Confirm',
      'Are you sure you want to log out?',
      UIConstants.SIGN_OUT,
      [UIConstants.NO],
      { centered: true },
    );
    if (response?.text !== UIConstants.SIGN_OUT) {
      return;
    }

    setTimeout(() => {
      this.closeSessionExpiryCountDown();
      this.authService.redirectToLogout();
    }, 300);
  }

  private closeSessionExpiryCountDown() {
    const sessionUpdate = { expiryAt: new Date().getTime(), sessionGoingToExpiry: false };
    this.store.dispatch(UpdateUserSession({ payload: sessionUpdate }));
  }

  ngOnDestroy(): void {
    this.oboUserAction$?.unsubscribe();
  }
}
